import React, { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useRouter } from 'next/router'
// Components
import { FullWidthLayout, SectionLayout } from '@/layouts'
import { Box, Grid, Typography } from '@mui/material'
import { sendContact } from '@/data'
import { GooglePolicy } from '@/components/ui/googlePolicy'
import { FormConsents } from '@/components/ui/formConsents/FormConsents'
import { ButtonAlert } from '@/components/ui'

// Helpers
import { SendFormWithCaptcha } from '@/helpers/sendFormWithCaptcha'
import { CellectAdress } from './CellectAdress'

// Styles
import * as Styled from './startConversation.styles'
import { useTheme } from '@cellectenergy/cellect-react-components'
import { InputText } from '@/components/input'
import { handleSubmitChecks } from './helper'
import { ROUTES } from '@/constants/routes'

export const StartConversation: FC = () => {
  const { t } = useTranslation('global')
  const { executeRecaptcha } = useGoogleReCaptcha()
  const theme = useTheme()

  const router = useRouter()

  const [privacyAccepted, setPrivacyAccepted] = useState(false)
  const [commercialCommunicationAccepted, setCommercialCommunicationAccepted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [alertState, setAlertState] = useState('')
  const [customerInfo, setCustomerInfo] = useState({
    firstName: '',
    lastName: '',
    company: '',
    email: '',
    country: '',
    message: '',
  })
  const [error, setError] = useState({
    email: false,
    message: false,
  })

  useEffect(() => {
    if (alertState === 'success') {
      const newWindow = window.open(ROUTES.thankYouContact, '_blank')
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        window.location.href = ROUTES.thankYouContact
      }
    }
  }, [alertState])

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!privacyAccepted) return

    handleSubmitChecks({
      customerInfo,
      commercialCommunicationAccepted,
      setError,
      setAlertState,
      setIsLoading,
      setCustomerInfo,
      SendFormWithCaptcha,
      executeRecaptcha,
      sendContact,
      router,
      t,
      error,
    })
  }

  return (
    <FullWidthLayout id="signUp" bgColor={theme.palette.common.corporateColors.primary.midNightBlue}>
      <SectionLayout>
        <Styled.MainContainer>
          <Grid container columnSpacing={{ xs: '2rem', md: '0', lg: '0' }} rowSpacing={{ xs: '1rem', md: 0 }}>
            {/* TITLE */}
            <Grid item xs={12} sm={6} md={6}>
              <Styled.TitleBox>
                <Typography
                  variant="display-small"
                  fontWeight={500}
                  color={theme.palette.common.corporateColors.primary.white}
                >
                  {t(`support.startConversation.title`)}
                </Typography>
                <Styled.InfoContainer>
                  <CellectAdress />
                </Styled.InfoContainer>
              </Styled.TitleBox>
            </Grid>
            {/* FORM */}
            <Grid item xs={12} sm={6} md={6}>
              <Styled.InnerContainer>
                <Typography
                  color={theme.palette.common.corporateColors.primary.white}
                  variant="headline-small"
                  fontWeight={500}
                >
                  {t(`support.startConversation.subtitle`)}
                </Typography>
                <Typography color={theme.palette.common.corporateColors.primary.white} variant="body-large">
                  {t(`support.startConversation.description`)}
                </Typography>
                <Styled.FormContainer onSubmit={handleSubmit}>
                  <Styled.FormBox>
                    <Box display="flex" gap="1rem" width={'100%'} flexDirection={{ xs: 'column', md: 'row' }}>
                      <InputText
                        required
                        autoComplete="off"
                        textcolor={theme.palette.common.corporateColors.primary.midNightBlue}
                        id={'filled-basic name'}
                        placeholder={t(`support.startConversation.form.firstName`).toString() + '*'}
                        variant="outlined"
                        type={'text'}
                        value={customerInfo.firstName}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            firstName: e.target.value,
                          })
                        }
                      />
                      <InputText
                        required
                        autoComplete="off"
                        textcolor={theme.palette.common.corporateColors.primary.midNightBlue}
                        id={'filled-basic email'}
                        placeholder={t(`support.startConversation.form.email`).toString() + '*' || ''}
                        variant="outlined"
                        helperText={error.email ? 'wrong email format' : ''}
                        FormHelperTextProps={{
                          sx: {
                            color: theme.palette.error.main,
                          },
                        }}
                        type={'email'}
                        value={customerInfo.email}
                        onChange={(e) =>
                          setCustomerInfo({
                            ...customerInfo,
                            email: e.target.value,
                          })
                        }
                      />
                    </Box>

                    <InputText
                      autoComplete="organization"
                      textcolor={theme.palette.common.corporateColors.primary.midNightBlue}
                      id={'filled-basic company'}
                      placeholder={t(`support.startConversation.form.company`).toString() || ''}
                      variant="outlined"
                      type={'text'}
                      value={customerInfo.company}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          company: e.target.value,
                        })
                      }
                    />

                    <InputText
                      placeholder={t(`support.startConversation.form.message`) + '*' || ''}
                      textcolor={theme.palette.common.corporateColors.primary.midNightBlue}
                      required
                      multiline
                      variant="outlined"
                      autoComplete="off"
                      rows={3}
                      onChange={(e) =>
                        setCustomerInfo({
                          ...customerInfo,
                          message: e.target.value,
                        })
                      }
                      value={customerInfo.message}
                      sx={{
                        '& .MuiInputBase-input': {
                          padding: '0',
                        },
                      }}
                    />

                    <GooglePolicy mobile={'100%'} desktop={'100%'} />
                  </Styled.FormBox>
                  <Styled.CheckBox>
                    <FormConsents
                      privacyAccepted={privacyAccepted}
                      onPrivacyChange={setPrivacyAccepted}
                      commercialCommunicationAccepted={commercialCommunicationAccepted}
                      onCommercialCommunicationChange={setCommercialCommunicationAccepted}
                      checkboxColor={theme.palette.common.corporateColors.primary.white}
                      idPrefix="start-conversation"
                    />
                  </Styled.CheckBox>

                  <ButtonAlert
                    type="submit"
                    color="primary"
                    size="medium"
                    isLoading={isLoading}
                    alertState={alertState}
                    sx={Styled.sxBtn}
                    sxBtnMui={{
                      width: {
                        xs: '100% !important',
                        md: '14.18rem !important',
                        lg: '20.68rem !important',
                      },
                      color: theme.palette.common.corporateColors.primary.midNightBlue,
                      backgroundColor: theme.palette.common.corporateColors.secondary.turquoise,
                    }}
                    disabled={!privacyAccepted}
                    loadingColor="info"
                  >
                    {t(`support.startConversation.button`)}
                  </ButtonAlert>
                </Styled.FormContainer>
                <Styled.FooterContainer>
                  <CellectAdress />
                </Styled.FooterContainer>
              </Styled.InnerContainer>
            </Grid>
          </Grid>
        </Styled.MainContainer>
      </SectionLayout>
    </FullWidthLayout>
  )
}
